import { useCallback, useState } from 'react'

const POSITION_KEY = 'qlearn:last-position'
const AUDIO_KEY = 'qlearn:audio-resume'

export type LastPosition = {
  surahNumber: number
  ayahNumber: number
}

export type AudioResume = {
  surahNumber: number
  ayahNumber: number
  currentTime: number
  reciter: string
}

function safeRead<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    return JSON.parse(raw) as T
  } catch { return null }
}

function safeWrite<T>(key: string, value: T): void {
  try { localStorage.setItem(key, JSON.stringify(value)) } catch { /* quota / private mode */ }
}

export function useReaderState() {
  const [lastPosition, setLastPositionState] = useState<LastPosition | null>(() => safeRead<LastPosition>(POSITION_KEY))
  const [audioResume, setAudioResumeState] = useState<AudioResume | null>(() => safeRead<AudioResume>(AUDIO_KEY))

  const recordPosition = useCallback((surahNumber: number, ayahNumber: number) => {
    const next = { surahNumber, ayahNumber }
    setLastPositionState(next)
    safeWrite(POSITION_KEY, next)
  }, [])

  const recordAudioPosition = useCallback((data: AudioResume) => {
    setAudioResumeState(data)
    safeWrite(AUDIO_KEY, data)
  }, [])

  const clearAudioResume = useCallback(() => {
    setAudioResumeState(null)
    try { localStorage.removeItem(AUDIO_KEY) } catch { /* ignore */ }
  }, [])

  return {
    lastPosition,
    audioResume,
    recordPosition,
    recordAudioPosition,
    clearAudioResume,
  }
}
